import React, { useEffect, useState, useRef, lazy, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm, ValidationError } from '@formspree/react';
import { Menu, X, Code2, Workflow, PlugZap, LineChart, CheckCircle2, MapPin, Mail, ArrowRight, ChevronDown, Phone } from 'lucide-react';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/mJjpcpCC1g76VpHo2yvP5c/logo_galop.png';
// --- SEO Hook ---
const SITE_URL = 'https://galop-solutions.fr';
const SITE_TITLE = 'GALOP Solutions — Logiciels métier sur mesure & automatisations en France';
const SITE_DESCRIPTION = "Agence française de logiciels sur mesure et d'automatisation. GALOP Solutions accompagne PME et indépendants pour simplifier leur quotidien et faire galoper leur activité.";
const SITE_KEYWORDS = 'logiciel sur mesure, automatisation, agence française, application métier, intégration API, tableau de bord, GALOP Solutions';
const useSEO = () => {
  useEffect(() => {
    // Lang on <html>
    document.documentElement.lang = 'fr';
    // Title
    document.title = SITE_TITLE;
    const upsertMeta = (selector: string, attrs: Record<string, string>) => {
      let el = document.head.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
        document.head.appendChild(el);
      } else {
        Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
      }
    };
    upsertMeta('meta[name="description"]', {
      name: 'description',
      content: SITE_DESCRIPTION
    });
    upsertMeta('meta[name="keywords"]', {
      name: 'keywords',
      content: SITE_KEYWORDS
    });
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: 'index, follow'
    });
    upsertMeta('meta[name="author"]', {
      name: 'author',
      content: 'GALOP Solutions'
    });
    // Open Graph
    upsertMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: SITE_TITLE
    });
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: SITE_DESCRIPTION
    });
    upsertMeta('meta[property="og:type"]', {
      property: 'og:type',
      content: 'website'
    });
    upsertMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: SITE_URL
    });
    upsertMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: LOGO_URL
    });
    upsertMeta('meta[property="og:locale"]', {
      property: 'og:locale',
      content: 'fr_FR'
    });
    upsertMeta('meta[property="og:site_name"]', {
      property: 'og:site_name',
      content: 'GALOP Solutions'
    });
    // Twitter Card
    upsertMeta('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: 'summary_large_image'
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: 'twitter:title',
      content: SITE_TITLE
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: SITE_DESCRIPTION
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: 'twitter:image',
      content: LOGO_URL
    });
    // Canonical link
    let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = SITE_URL;
    // JSON-LD structured data
    const jsonLdId = 'galop-jsonld';
    let jsonLd = document.getElementById(jsonLdId) as HTMLScriptElement | null;
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.type = 'application/ld+json';
      jsonLd.id = jsonLdId;
      document.head.appendChild(jsonLd);
    }
    const services = [{
      name: 'Logiciels métier sur mesure',
      description: "Conception et développement d'applications web sur mesure pour les PME et indépendants, calibrées sur vos processus métier."
    }, {
      name: 'Automatisation de processus',
      description: 'Mise en place de flux de travail automatisés pour supprimer les tâches répétitives et libérer du temps à vos équipes.'
    }, {
      name: 'Intégrations & API',
      description: 'Interconnexion de vos logiciels (CRM, ERP, comptabilité) pour synchroniser vos données et éliminer les doubles saisies.'
    }, {
      name: 'Tableaux de bord & reporting',
      description: 'Création de tableaux de bord visuels regroupant vos indicateurs clés pour piloter vos performances.'
    }];
    jsonLd.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [{
        '@type': 'LocalBusiness',
        '@id': `${SITE_URL}/#organization`,
        name: 'GALOP Solutions',
        legalName: 'GALOP Solutions SARL',
        url: SITE_URL,
        logo: LOGO_URL,
        image: LOGO_URL,
        description: SITE_DESCRIPTION,
        email: 'contact@galop-solutions.fr',
        foundingDate: '2024',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '9 rue Mirabeau',
          postalCode: '66210',
          addressLocality: 'Mont-Louis',
          addressCountry: 'FR'
        },
        areaServed: {
          '@type': 'Country',
          name: 'France'
        },
        identifier: [{
          '@type': 'PropertyValue',
          name: 'SIRET',
          value: '94151294900012'
        }, {
          '@type': 'PropertyValue',
          name: 'RCS',
          value: 'Perpignan B 941 512 949'
        }],
        founder: {
          '@type': 'Person',
          name: 'Hugo Langlais'
        },
        sameAs: ['https://www.linkedin.com/company/galop-solutions/']
      }, {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: 'GALOP Solutions',
        inLanguage: 'fr-FR',
        publisher: {
          '@id': `${SITE_URL}/#organization`
        }
      }, ...services.map(s => ({
        '@type': 'Service',
        name: s.name,
        description: s.description,
        provider: {
          '@id': `${SITE_URL}/#organization`
        },
        areaServed: {
          '@type': 'Country',
          name: 'France'
        }
      }))]
    });
    // Preconnect & preload for performance
    const upsertLink = (rel: string, href: string, attrs: Record<string, string> = {}) => {
      const selector = `link[rel="${rel}"][href="${href}"]`;
      if (document.head.querySelector(selector)) return;
      const link = document.createElement('link');
      link.rel = rel;
      link.href = href;
      Object.entries(attrs).forEach(([k, v]) => link.setAttribute(k, v));
      document.head.appendChild(link);
    };
    upsertLink('preconnect', 'https://cdn.magicpatterns.com', {
      crossorigin: ''
    });
    upsertLink('dns-prefetch', 'https://cdn.magicpatterns.com');
    upsertLink('preconnect', 'https://formspree.io');
    upsertLink('dns-prefetch', 'https://formspree.io');
    upsertLink('preload', LOGO_URL, {
      as: 'image',
      fetchpriority: 'high'
    });
  }, []);
};
// --- Reusable Animation Wrapper ---
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-2" data-mp="26mex4u6fdvag">
    {children}
  </motion.div>;
// --- Header ---
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    name: 'Services',
    href: '#services'
  }, {
    name: 'Processus',
    href: '#processus'
  }, {
    name: 'À propos',
    href: '#about'
  }];
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-3" data-mp="mvdwpra6sk4a">
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between" data-id="element-4" data-mp="1yb2g68o8f10c">
        <a href="#" aria-label="Accueil GALOP Solutions" className="flex items-center gap-3 group" data-id="element-5" data-mp="1ydey4ltzpbde">
          <img src={LOGO_URL} alt="Logo de GALOP Solutions" width={40} height={40} loading="eager" decoding="async" className="w-10 h-10 object-contain transition-transform group-hover:scale-105" data-id="element-6" data-mp="2debzs2r74ur4" />
          <span className="font-bold text-xl text-[#2B1D14]" data-id="element-7" data-mp="1zn7anulgfkv1">
            GALOP Solutions
          </span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-id="element-8" data-mp="1uovyhd7osy9n">
          {navLinks.map(link => <a key={link.name} href={link.href} className="text-[#2B1D14]/80 hover:text-[#2B1D14] font-medium transition-colors" data-id="element-9" data-mp="1pn6kbop644hc">
              {link.name}
            </a>)}
          <a href="#contact" className="px-5 py-2.5 bg-[#2B1D14] text-[#F5EBD7] rounded-lg font-semibold hover:bg-[#1F1813] transition-colors" data-id="element-10" data-mp="3f3621u6ur28l">
            Contact
          </a>
        </nav>

        {/* Mobile Toggle */}
        <button className="md:hidden text-[#2B1D14]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-11" data-mp="cmrzr13pol6r">
          {mobileMenuOpen ? <X size={28} data-id="element-12" data-mp="mnapudlvcxx4" /> : <Menu size={28} data-id="element-13" data-mp="mlpvop3gvgzs" />}
        </button>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence data-id="element-14" data-mp="hn6bou8fofa2">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-white border-b border-[#2B1D14]/10 overflow-hidden" data-id="element-15" data-mp="hlle0vqtog7z">
            <div className="flex flex-col px-6 py-4 gap-4" data-id="element-16" data-mp="3v19mzikkupc7">
              {navLinks.map(link => <a key={link.name} href={link.href} onClick={() => setMobileMenuOpen(false)} className="text-lg font-medium text-[#2B1D14]/80 hover:text-[#2B1D14]" data-id="element-17" data-mp="37ax5bfzj5e8f">
                  {link.name}
                </a>)}
              <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="inline-block text-center px-5 py-3 bg-[#2B1D14] text-[#F5EBD7] rounded-lg font-semibold mt-2" data-id="element-18" data-mp="18v6fqbvqnuez">
                Contact
              </a>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
};
// --- Hero ---
const Hero = () => {
  return <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 px-6 overflow-hidden flex flex-col items-center text-center" data-id="element-19" data-mp="1rm7diwl4hjwy">
      <FadeIn className="flex flex-col items-center max-w-4xl mx-auto" data-id="element-20" data-mp="1wjfwgze3hrz9">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F5EBD7] text-[#2B1D14]/80 text-sm font-medium mb-8 border border-[#2B1D14]/10" data-id="element-21" data-mp="g5zgtbsdh2t7">
          <span aria-hidden="true" className="inline-flex h-3.5 w-5 overflow-hidden rounded-sm border border-[#2B1D14]/10" data-id="element-22" data-mp="l393ijodn0dc">
            <span className="flex-1 bg-[#0055A4]" data-id="element-23" data-mp="2hvye2pytc659" />
            <span className="flex-1 bg-white" data-id="element-24" data-mp="2hw80k3knfv67" />
            <span className="flex-1 bg-[#EF4135]" data-id="element-25" data-mp="2d4pytdasxqk6" />
          </span>
          Basé dans les Vosges
        </div>

        <img src={LOGO_URL} alt="Logo de GALOP Solutions, agence française de logiciels sur mesure" width={224} height={224} decoding="async"
      // @ts-ignore - fetchpriority is valid HTML
      fetchpriority="high" className="w-40 h-40 md:w-56 md:h-56 mb-8 drop-shadow-xl" data-id="element-26" data-mp="29466cn16bb4d" />

        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-[#2B1D14] leading-tight mb-6 tracking-tight" data-id="element-27" data-mp="1u86qgq7to6oo">
          Faites galoper votre activité
        </h1>

        <p className="text-xl md:text-2xl text-[#2B1D14]/70 max-w-2xl mb-10 leading-relaxed" data-id="element-28" data-mp="3ojle91h9vw1s">
          Nous concevons les logiciels sur mesure et les automatisations qui
          simplifient votre quotidien.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto" data-id="element-29" data-mp="168o5vd18f69n">
          <a href="#contact" className="inline-flex justify-center items-center gap-2 px-8 py-4 bg-[#2B1D14] text-[#F5EBD7] rounded-xl font-semibold text-lg hover:bg-[#1F1813] transition-all hover:scale-105 active:scale-95" data-id="element-30" data-mp="1b8ajhnkz2oiz">
            Discuter de mon projet
          </a>
          <a href="#services" className="inline-flex justify-center items-center gap-2 px-8 py-4 bg-[#F5EBD7] text-[#2B1D14] rounded-xl font-semibold text-lg hover:bg-[#EBE0C9] transition-all" data-id="element-31" data-mp="ndr2g3i3tt2t">
            Voir nos services
          </a>
        </div>
      </FadeIn>
    </section>;
};
// --- Services ---
const Services = () => {
  const services = [{
    icon: <Code2 size={32} data-id="element-32" data-mp="27rhxtvhf8n0x" />,
    title: 'Logiciels métier sur mesure',
    desc: 'Une application web développée exclusivement pour votre activité. Pas de gadget, uniquement des fonctionnalités dont vous avez réellement besoin, calibrées sur vos habitudes de travail.'
  }, {
    icon: <Workflow size={32} data-id="element-33" data-mp="26c5fb2ykfucj" />,
    title: 'Automatisation de processus',
    desc: "Supprimez les actions manuelles et répétitives de vos journées. Nous configurons des flux de travail qui s'exécutent automatiquement en arrière-plan pour libérer du temps à vos équipes."
  }, {
    icon: <PlugZap size={32} data-id="element-34" data-mp="2u78fg72tlk8b" />,
    title: 'Intégrations & API',
    desc: 'Faites communiquer vos logiciels actuels entre eux. Nous branchons votre CRM, votre ERP ou votre comptabilité pour que vos données soient synchronisées partout, évitant ainsi les doubles saisies et les erreurs.'
  }, {
    icon: <LineChart size={32} data-id="element-35" data-mp="2yzrtt69c0je6" />,
    title: 'Tableaux de bord & reporting',
    desc: 'Regroupez vos indicateurs clés sur un seul écran visuel. Vous pilotez vos performances et prenez vos décisions avec des données fiables et à jour.'
  }];
  return <section id="services" className="py-20 md:py-32 px-6 bg-[#F5EBD7]" data-id="element-36" data-mp="3rl84omr2a450">
      <div className="max-w-7xl mx-auto" data-id="element-37" data-mp="3rjww89mo887k">
        <FadeIn className="text-center max-w-2xl mx-auto mb-16" data-id="element-38" data-mp="edofhnsct3c">
          <h2 className="text-3xl md:text-5xl font-bold text-[#2B1D14] mb-6" data-id="element-39" data-mp="3ilbl2zjj3vgt">
            Nos Services
          </h2>
          <p className="text-lg text-[#2B1D14]/70" data-id="element-40" data-mp="3mm3rll10nm9e">
            Nous créons des solutions concrètes pour éliminer vos blocages
            opérationnels et optimiser votre quotidien.
          </p>
        </FadeIn>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8" data-id="element-41" data-mp="3dn09okdl5cwh">
          {services.map((service, idx) => <FadeIn key={idx} delay={idx * 0.1} data-id="element-42" data-mp="2yydjtoqc12r0" data-mpi={idx}>
              <div className="bg-white p-8 rounded-2xl h-full border border-[#2B1D14]/5 hover:shadow-lg transition-shadow group" data-id="element-43" data-mp="33w5dan6mkwxb">
                <div className="w-16 h-16 bg-[#2B1D14] text-[#F5EBD7] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-44" data-mp="2pw4c16mg7nhb">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-[#2B1D14] mb-3" data-id="element-45" data-mp="10g0ngn7h57ge">
                  {service.title}
                </h3>
                <p className="text-[#2B1D14]/70 leading-relaxed" data-id="element-46" data-mp="vmfmcmal3xcz">
                  {service.desc}
                </p>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
};
// --- Process ---
const Process = () => {
  const steps = [{
    title: 'Écoute',
    desc: "Nous analysons votre quotidien, vos outils actuels et vos processus afin d'identifier précisément les blocages à éliminer."
  }, {
    title: 'Conception',
    desc: 'Nous partons de votre idée pour concevoir ensemble la solution idéale et créons les premières maquettes visuelles.'
  }, {
    title: 'Développement',
    desc: "Nous bâtissons une solution solide et évolutive. Vous suivez chaque étape pour l'ajuster et l'améliorer au fil de vos idées."
  }, {
    title: 'Lancement',
    desc: 'Nous déployons votre solution, formons vos équipes à sa prise en main et restons à vos côtés pour assurer ses évolutions.'
  }];
  return <section id="processus" className="py-20 md:py-32 px-6" data-id="element-47" data-mp="2aluykdccko12">
      <div className="max-w-7xl mx-auto" data-id="element-48" data-mp="2emyhj8poz06i">
        <FadeIn className="text-center max-w-2xl mx-auto mb-16" data-id="element-49" data-mp="2el3rxh3h6jw3">
          <h2 className="text-3xl md:text-5xl font-bold text-[#2B1D14] mb-6" data-id="element-50" data-mp="2jeq7pi4xr3w4">
            Notre Approche
          </h2>
          <p className="text-lg text-[#2B1D14]/70" data-id="element-51" data-mp="2ocrplzqmkx60">
            Une méthode simple pour passer d'une idée à la réalité.
          </p>
        </FadeIn>

        <div className="grid md:grid-cols-4 gap-8 relative" data-id="element-52" data-mp="2yao0cqfnhq3l">
          {/* Connecting line for desktop */}
          <div className="hidden md:block absolute top-10 left-[10%] right-[10%] h-0.5 bg-[#2B1D14]/10 z-0" data-id="element-53" data-mp="32d2h94zrefs8"></div>

          {steps.map((step, idx) => <FadeIn key={idx} delay={idx * 0.15} className="relative z-10 flex flex-col items-center text-center" data-id="element-54" data-mp="13z955u3veygg" data-mpi={idx}>
              <div className="w-20 h-20 bg-[#2B1D14] text-[#F5EBD7] rounded-full flex items-center justify-center text-2xl font-bold mb-6 shadow-xl border-4 border-white" data-id="element-55" data-mp="p0yd0id484nl">
                {idx + 1}
              </div>
              <h3 className="text-xl font-bold text-[#2B1D14] mb-3" data-id="element-56" data-mp="xu1q2pjumfhp">
                {step.title}
              </h3>
              <p className="text-[#2B1D14]/70 text-sm leading-relaxed" data-id="element-57" data-mp="tvltpv1vnurm">
                {step.desc}
              </p>
            </FadeIn>)}
        </div>
      </div>
    </section>;
};
// --- About ---
const About = () => {
  const stats = ['Une approche collée à vos besoins', 'Le sur-mesure accessible', 'Des solutions évolutives', 'Accompagnement long terme'];
  return <section id="about" className="py-20 md:py-32 px-6 bg-[#F5EBD7]" data-id="element-58" data-mp="1qjib32552u3w">
      <div className="max-w-7xl mx-auto" data-id="element-59" data-mp="1qhnq5ntvb1zt">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-60" data-mp="1lknsa07695js">
          <FadeIn data-id="element-61" data-mp="1lmi3unwjzlb7">
            <h2 className="text-3xl md:text-5xl font-bold text-[#2B1D14] mb-6" data-id="element-62" data-mp="1u1fswkb7nxi4">
              Pourquoi choisir GALOP ?
            </h2>
            <div className="space-y-6 text-lg text-[#2B1D14]/80 leading-relaxed" data-id="element-63" data-mp="1y42ngsz9nwj0">
              <p data-id="element-64" data-mp="1k3runkeec7v7">
                Chez GALOP Solutions, nous croyons que la technologie doit
                s'adapter à votre entreprise, et non l'inverse. Trop souvent,
                les entreprises perdent un temps précieux à jongler entre des
                outils inadaptés ou des fichiers Excel complexes.
              </p>
              <p data-id="element-65" data-mp="2hqh58in5zsso">
                Notre mission est de vous simplifier le quotidien. En tant
                qu'agence à taille humaine, nous privilégions la proximité,
                l'écoute et la qualité d'exécution.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-66" data-mp="2vqi3dqc1om4g">
            <div className="bg-white p-8 md:p-10 rounded-3xl shadow-sm border border-[#2B1D14]/5" data-id="element-67" data-mp="2vpgu9rela9pd">
              <h3 className="text-2xl font-bold text-[#2B1D14] mb-8" data-id="element-68" data-mp="30it610y66m0p">
                Nos engagements
              </h3>
              <div className="space-y-4" data-id="element-69" data-mp="vzknzdgikb92">
                {stats.map((stat, idx) => <div key={idx} className="flex items-center gap-4" data-id="element-70" data-mp="100wfjoiy76jw" data-mpi={idx}>
                    <CheckCircle2 className="text-[#2B1D14] shrink-0" size={24} data-id="element-71" data-mp="m6m05xsucoqi" />
                    <span className="text-lg font-medium text-[#2B1D14]" data-id="element-72" data-mp="c97hrsncxpvk">
                      {stat}
                    </span>
                  </div>)}
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
};
// --- FAQ ---
const FAQ = () => {
  const faqs = [{
    q: 'Combien coûte un projet sur mesure ?',
    a: "Le coût dépend entièrement de la complexité de vos besoins. Un outil simple d'automatisation peut commencer à quelques milliers d'euros, tandis qu'un logiciel métier complet sera plus conséquent. Nous proposons toujours un devis détaillé après la phase de cadrage."
  }, {
    q: 'Combien de temps prend un projet ?',
    a: 'En général, nos projets durent entre 1 et 4 mois. Nous privilégions une approche itérative : nous livrons rapidement une première version fonctionnelle (MVP) que nous améliorons ensuite avec vos retours.'
  }, {
    q: 'Travaillez-vous à distance ?',
    a: 'Oui, nous accompagnons des clients dans toute la France grâce aux outils de visioconférence et de collaboration en ligne.'
  }, {
    q: 'Quelles technologies utilisez-vous ?',
    a: 'Nous utilisons des technologies web modernes, robustes et éprouvées (React, Node.js, bases de données cloud, etc.) pour garantir des applications rapides, sécurisées et maintenables sur le long terme.'
  }, {
    q: 'Proposez-vous de la maintenance ?',
    a: "Absolument. Un logiciel vit et évolue. Nous proposons des forfaits d'hébergement, de maintenance et de support pour assurer le bon fonctionnement de votre outil et le faire évoluer selon vos futurs besoins."
  }];
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return <section id="faq" className="py-20 md:py-32 px-6" data-id="element-73" data-mp="3a5rjanwcus0z">
      <div className="max-w-3xl mx-auto" data-id="element-74" data-mp="3f4cl2rr99jrj">
        <FadeIn className="text-center mb-16" data-id="element-75" data-mp="3f39wwrlbhvfb">
          <h2 className="text-3xl md:text-5xl font-bold text-[#2B1D14] mb-6" data-id="element-76" data-mp="3k35yylagfcs8">
            Questions Fréquentes
          </h2>
        </FadeIn>

        <div className="space-y-4" data-id="element-77" data-mp="3o3pz7xqbqwil">
          {faqs.map((faq, idx) => <FadeIn key={idx} delay={idx * 0.1} data-id="element-78" data-mp="3t3c8jw317sm4" data-mpi={idx}>
              <div className="bg-[#F5EBD7]/60 rounded-2xl overflow-hidden border border-[#2B1D14]/10 transition-colors hover:bg-[#F5EBD7]" data-id="element-79" data-mp="3t217w54vv0z8">
                <button onClick={() => setOpenIdx(openIdx === idx ? null : idx)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-80" data-mp="6nzwrp0zn0a6">
                  <span className="font-bold text-lg text-[#2B1D14] pr-8" data-id="element-81" data-mp="fpzdshc064d4">
                    {faq.q}
                  </span>
                  <ChevronDown className={`text-[#2B1D14] transition-transform duration-300 shrink-0 ${openIdx === idx ? 'rotate-180' : ''}`} size={24} data-id="element-82" data-mp="208bdkflmpee2" />
                </button>
                <AnimatePresence data-id="element-83" data-mp="1m7h0hjnmyp92">
                  {openIdx === idx && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-84" data-mp="1m9bkmvr32dc3">
                      <div className="px-6 pb-5 text-[#2B1D14]/70 leading-relaxed" data-id="element-85" data-mp="2xuh0dgi2fgpl">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
};
// --- Contact ---
const Contact = () => {
  const [state, handleSubmit] = useForm('xredwbrw');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const openPrivacy = () => {
    window.dispatchEvent(new CustomEvent('open-privacy'));
  };
  return <section id="contact" className="py-20 md:py-32 px-6 bg-[#2B1D14] text-[#F5EBD7]" data-id="element-86" data-mp="3621xiyacp8db">
      <div className="max-w-7xl mx-auto" data-id="element-87" data-mp="2rdxh60yrt8xp">
        <div className="grid lg:grid-cols-2 gap-16" data-id="element-88" data-mp="2rcw0ngyzju8l">
          <FadeIn data-id="element-89" data-mp="2wbyvy9p5ym3d">
            <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-90" data-mp="2wbgtgvbwrbn0">
              Prêt à donner vie à votre projet ?
            </h2>
            <p className="text-lg text-[#F5EBD7]/70 mb-10" data-id="element-91" data-mp="3k0rxqmsgvo5w">
              Décrivez nous votre besoin et comment nous pourrions vous aider.
            </p>

            <address className="space-y-6 not-italic" data-id="element-92" data-mp="1dd0ytmvkxkcg">
              <a href="mailto:contact@galop-solutions.fr" className="flex items-center gap-4 group" data-id="element-93" data-mp="1dbhjpzlntfc9">
                <div className="w-12 h-12 bg-[#F5EBD7]/10 rounded-full flex items-center justify-center group-hover:bg-[#F5EBD7]/20 transition-colors" data-id="element-94" data-mp="1m52xpgq02skm">
                  <Mail size={24} data-id="element-95" data-mp="1i6ww0n40r36k" />
                </div>
                <div data-id="element-96" data-mp="ufrfe5q986v7">
                  <div className="text-sm text-[#F5EBD7]/50 mb-1" data-id="element-97" data-mp="uf88rl6214wh">Email</div>
                  <div className="font-medium text-lg" data-id="element-98" data-mp="ph6owy0t11jy">
                    contact@galop-solutions.fr
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-4" data-id="element-99" data-mp="yg1xuhv8gpqy">
                <div className="w-12 h-12 bg-[#F5EBD7]/10 rounded-full flex items-center justify-center" data-id="element-100" data-mp="yio3ud2h6x72">
                  <MapPin size={24} data-id="element-101" data-mp="2ewwbl1il71yn" />
                </div>
                <div data-id="element-102" data-mp="29uoz8atuhjvs">
                  <div className="text-sm text-[#F5EBD7]/50 mb-1" data-id="element-103" data-mp="29vgtnibphbkh">
                    Conception
                  </div>
                  <div className="font-medium text-lg" data-id="element-104" data-mp="17kn76muoxwpy">100% Française</div>
                </div>
              </div>
            </address>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-105" data-mp="1gkabphipvin8">
            <form onSubmit={handleSubmit} className="bg-[#F5EBD7] p-8 rounded-3xl text-[#2B1D14]" data-id="element-106" data-mp="1ggw702mkyxcm">
              <h3 className="text-2xl font-bold mb-6" data-id="element-107" data-mp="oscko3kcgbe0">
                Envoyez-nous un message
              </h3>

              <div className="space-y-4" data-id="element-108" data-mp="sug176lve665">
                <div data-id="element-109" data-mp="srbqrrhtae71">
                  <label htmlFor="name" className="block text-sm font-medium mb-2" data-id="element-110" data-mp="xsra9f4cccnb">
                    Nom complet
                  </label>
                  <input type="text" id="name" name="name" required className="w-full px-4 py-3 rounded-xl bg-white/50 border border-[#2B1D14]/20 focus:outline-none focus:ring-2 focus:ring-[#2B1D14] transition-all" data-id="element-111" data-mp="543ir1t00r1b" />
                  <ValidationError field="name" prefix="Nom" errors={state.errors} className="text-sm text-red-700 mt-1" data-id="element-112" data-mp="3tqqcvp3vsjl2" />
                </div>

                <div data-id="element-113" data-mp="3krd3sxjo5x0v">
                  <label htmlFor="email" className="block text-sm font-medium mb-2" data-id="element-114" data-mp="3fpntjk49tndu">
                    Email
                  </label>
                  <input type="email" id="email" name="email" required className="w-full px-4 py-3 rounded-xl bg-white/50 border border-[#2B1D14]/20 focus:outline-none focus:ring-2 focus:ring-[#2B1D14] transition-all" data-id="element-115" data-mp="35zus3bxueonb" />
                  <ValidationError field="email" prefix="Email" errors={state.errors} className="text-sm text-red-700 mt-1" data-id="element-116" data-mp="2s1o2sk6ylwz9" />
                </div>

                <div data-id="element-117" data-mp="oodmk8skkrqn">
                  <label htmlFor="message" className="block text-sm font-medium mb-2" data-id="element-118" data-mp="ari1tj2yhbur">
                    Votre projet
                  </label>
                  <textarea id="message" name="message" required rows={4} className="w-full px-4 py-3 rounded-xl bg-white/50 border border-[#2B1D14]/20 focus:outline-none focus:ring-2 focus:ring-[#2B1D14] transition-all resize-none" data-id="element-119" data-mp="10nionl0asdj"></textarea>
                  <ValidationError field="message" prefix="Message" errors={state.errors} className="text-sm text-red-700 mt-1" data-id="element-120" data-mp="3s53z76inq31b" />
                </div>

                <label htmlFor="privacy" className="flex items-start gap-3 text-sm text-[#2B1D14]/80 leading-relaxed cursor-pointer select-none" data-id="element-121" data-mp="2zm1ilwns9qgz">
                  <input type="checkbox" id="privacy" required checked={privacyAccepted} onChange={e => setPrivacyAccepted(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 rounded border-[#2B1D14]/30 text-[#2B1D14] focus:ring-2 focus:ring-[#2B1D14] cursor-pointer" data-id="element-122" data-mp="38mpx0sh39sue" />
                  <span data-id="element-123" data-mp="3s8cmbg6os7i0">
                    J'accepte la{' '}
                    <button type="button" onClick={openPrivacy} className="underline font-medium hover:text-[#2B1D14] transition-colors" data-id="element-124" data-mp="3na1aizwtzmx5">
                      politique de confidentialité
                    </button>{' '}
                    de GALOP Solutions.
                  </span>
                </label>

                <button type="submit" disabled={state.submitting || state.succeeded} className="w-full py-4 bg-[#2B1D14] text-[#F5EBD7] rounded-xl font-bold text-lg hover:bg-[#1F1813] transition-colors disabled:opacity-70 flex justify-center items-center" data-id="element-125" data-mp="xif42t2rfrlq">
                  {state.succeeded ? 'Message envoyé ! ✓' : state.submitting ? 'Envoi en cours...' : 'Envoyer'}
                </button>
              </div>
            </form>
          </FadeIn>
        </div>
      </div>
    </section>;
};
// --- Footer ---
const Footer = () => {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const privacyRef = useRef(null);
  const legalRef = useRef(null);
  const shouldScrollPrivacyRef = useRef(false);
  const shouldScrollLegalRef = useRef(false);
  const scrollToPrivacy = () => {
    if (privacyRef.current) {
      privacyRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  const scrollToLegal = () => {
    if (legalRef.current) {
      legalRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  const togglePrivacy = () => {
    setPrivacyOpen(open => {
      const next = !open;
      if (next) shouldScrollPrivacyRef.current = true;
      return next;
    });
  };
  const toggleLegal = () => {
    setLegalOpen(open => {
      const next = !open;
      if (next) shouldScrollLegalRef.current = true;
      return next;
    });
  };
  useEffect(() => {
    const handleOpen = () => {
      shouldScrollPrivacyRef.current = true;
      setPrivacyOpen(open => {
        if (open) {
          // Panel already open: scroll immediately, no animation pending
          scrollToPrivacy();
          shouldScrollPrivacyRef.current = false;
        }
        return true;
      });
    };
    window.addEventListener('open-privacy', handleOpen);
    return () => window.removeEventListener('open-privacy', handleOpen);
  }, []);
  const privacySections = [{
    title: '1. Collecte des données',
    body: 'Nous collectons uniquement les informations que vous nous transmettez via notre formulaire de contact : votre nom complet, votre adresse email et la description de votre projet.'
  }, {
    title: '2. Utilisation et transfert des informations',
    body: "Ces données ne sont pas stockées sur le serveur de notre site internet. Pour assurer l'envoi sécurisé de vos messages sans intermédiaire technique sur notre site, nous utilisons le service Formspree.\n\nLorsque vous validez le formulaire, vos informations lui sont temporairement transmises de manière sécurisée afin de générer l'e-mail qui nous est directement envoyé sur notre messagerie professionnelle. Ces données sont utilisées exclusivement pour répondre à votre demande et échanger sur votre projet. Elles ne seront jamais vendues, louées ou transmises à d'autres tiers."
  }, {
    title: '3. Durée de conservation',
    body: 'Vos informations sont conservées dans notre messagerie professionnelle pendant la durée nécessaire au traitement de notre échange, ou supprimées immédiatement sur simple demande de votre part.'
  }, {
    title: '4. Vos droits',
    body: 'Conformément au RGPD, vous disposez d’un droit d’accès, de modification et de suppression de vos données. Pour exercer ce droit, il vous suffit de nous envoyer un email à : contact@galop-solutions.fr.'
  }];
  return <footer className="bg-[#1F1813] text-[#F5EBD7]/60 border-t border-white/10" data-id="element-126" data-mp="58fqxqsr2z9">
      <AnimatePresence initial={false} data-id="element-127" data-mp="nvcil2w5uyp7">
        {legalOpen && <motion.div key="legal-panel" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} onAnimationComplete={() => {
        if (legalOpen && shouldScrollLegalRef.current) {
          shouldScrollLegalRef.current = false;
          scrollToLegal();
        }
      }} className="overflow-hidden border-b border-white/10" data-id="element-128" data-mp="nu1f746mnsym">
            <div ref={legalRef} id="legal-notice" className="max-w-3xl mx-auto px-6 py-12 scroll-mt-24" data-id="element-129" data-mp="3ewwctu1ivnee">
              <div className="flex items-start justify-between gap-6 mb-8" data-id="element-130" data-mp="353xp0eduo0vu">
                <div data-id="element-131" data-mp="6eytl0l13iyn">
                  <h3 className="text-2xl font-bold text-[#F5EBD7] mb-2" data-id="element-132" data-mp="6ficokmf0de9">
                    Mentions Légales
                  </h3>
                  <p className="text-sm text-[#F5EBD7]/60" data-id="element-133" data-mp="1m5q0g1aqgo5">GALOP Solutions</p>
                </div>
                <button onClick={() => setLegalOpen(false)} aria-label="Fermer les mentions légales" className="text-[#F5EBD7]/60 hover:text-[#F5EBD7] transition-colors shrink-0" data-id="element-134" data-mp="91ho29tgus3v">
                  <X size={22} data-id="element-135" data-mp="mzz6bs0lrcee" />
                </button>
              </div>
              <div className="space-y-6 text-sm leading-relaxed text-[#F5EBD7]/70" data-id="element-136" data-mp="ryiv006kz2ag">
                <div data-id="element-137" data-mp="wx3u1wa3jfxb">
                  <h4 className="font-semibold text-[#F5EBD7] mb-2" data-id="element-138" data-mp="wwc16t7vvwaq">
                    1. Éditeur du site
                  </h4>
                  <p className="mb-3" data-id="element-139" data-mp="11oc5exuzlbd4">
                    Ce site internet est édité par la société GALOP Solutions, SARL au capital de 1 000 €, dont le siège social est situé au 44 quai des bons enfants, 88000 Épinal.
                  </p>
                  <ul className="space-y-1" data-id="element-140" data-mp="1fnlcn817sv4y">
                    <li data-id="element-141" data-mp="1fod90l2mywwa">
                      <span className="text-[#F5EBD7]/90 font-medium" data-id="element-142" data-mp="1flimmo02b208">
                        SIRET :
                      </span>{' '}
                      941 512 949 00038
                    </li>
                    <li data-id="element-143" data-mp="37n1trx8b61q1">
                      <span className="text-[#F5EBD7]/90 font-medium" data-id="element-144" data-mp="3lg9klmoket6o">
                        RCS :
                      </span>{' '}
                      Épinal B 941 512 949
                    </li>
                    <li data-id="element-145" data-mp="3vegytxrgyx4z">
                      <span className="text-[#F5EBD7]/90 font-medium" data-id="element-146" data-mp="3vdo11mahnhyz">
                        Numéro de TVA intracommunautaire :
                      </span>{' '}
                      Exonéré de TVA
                    </li>
                    <li data-id="element-147" data-mp="89hns898wjmd">
                      <span className="text-[#F5EBD7]/90 font-medium" data-id="element-148" data-mp="87ncllns05vh">
                        Contact :
                      </span>{' '}
                      <a href="mailto:contact@galop-solutions.fr" className="underline hover:text-[#F5EBD7] transition-colors" data-id="element-149" data-mp="2a0kyexj4xy6x">
                        contact@galop-solutions.fr
                      </a>
                    </li>
                    <li data-id="element-150" data-mp="pjljt6ywtjxe">
                      <span className="text-[#F5EBD7]/90 font-medium" data-id="element-151" data-mp="pitxkmr7cyjs">
                        Directeur de la publication :
                      </span>{' '}
                      Hugo Langlais
                    </li>
                  </ul>
                </div>
                <div data-id="element-152" data-mp="1t7ydzatyjbs">
                  <h4 className="font-semibold text-[#F5EBD7] mb-2" data-id="element-153" data-mp="1spxuqh4xiy2">
                    2. Hébergeur du site
                  </h4>
                  <p className="mb-2" data-id="element-154" data-mp="6qrc8y3m8u37">
                    Le site est hébergé par la société Magic Patterns.
                  </p>
                  <p data-id="element-155" data-mp="3szskr5pxx32h">
                    <span className="text-[#F5EBD7]/90 font-medium" data-id="element-156" data-mp="3szak7ww8w2or">
                      Site web :
                    </span>{' '}
                    <a href="https://www.magicpatterns.com/" target="_blank" rel="noreferrer" className="underline hover:text-[#F5EBD7] transition-colors" data-id="element-157" data-mp="1n7vknimym1mx">
                      https://www.magicpatterns.com/
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>}
        {privacyOpen && <motion.div key="privacy-panel" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} onAnimationComplete={() => {
        if (privacyOpen && shouldScrollPrivacyRef.current) {
          shouldScrollPrivacyRef.current = false;
          scrollToPrivacy();
        }
      }} className="overflow-hidden border-b border-white/10" data-id="element-158" data-mp="9obkv1vcjrh">
            <div ref={privacyRef} id="privacy-policy" className="max-w-3xl mx-auto px-6 py-12 scroll-mt-24" data-id="element-159" data-mp="1k4ixd1ulwk66">
              <div className="flex items-start justify-between gap-6 mb-8" data-id="element-160" data-mp="1t08f90wuiwet">
                <div data-id="element-161" data-mp="1xy9wrhemino6">
                  <h3 className="text-2xl font-bold text-[#F5EBD7] mb-2" data-id="element-162" data-mp="1xwp3dv41parw">
                    Politique de Confidentialité
                  </h3>
                  <p className="text-sm text-[#F5EBD7]/60" data-id="element-163" data-mp="159cqkcnoshbs">GALOP Solutions</p>
                </div>
                <button onClick={() => setPrivacyOpen(false)} aria-label="Fermer la politique de confidentialité" className="text-[#F5EBD7]/60 hover:text-[#F5EBD7] transition-colors shrink-0" data-id="element-164" data-mp="1a8flh49yd761">
                  <X size={22} data-id="element-165" data-mp="2ln2unjet35lf" />
                </button>
              </div>
              <div className="space-y-6" data-id="element-166" data-mp="2qlmlny89b7kn">
                {privacySections.map(section => <div key={section.title} data-id="element-167" data-mp="2o0e3velrvd2i">
                    <h4 className="font-semibold text-[#F5EBD7] mb-2" data-id="element-168" data-mp="2o1fbta7hrjg0">
                      {section.title}
                    </h4>
                    <div className="text-sm leading-relaxed text-[#F5EBD7]/70 space-y-3" data-id="element-169" data-mp="2wzho4lwodyil">
                      {section.body.split('\n\n').map((para, i) => <p key={i} data-id="element-170" data-mp="31x10thdq638f" data-mpi={i}>{para}</p>)}
                    </div>
                  </div>)}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-171" data-mp="3fqsbit36zt5q">
        <div className="flex items-center gap-3" data-id="element-172" data-mp="3kq4zjh5sv69w">
          <img src={LOGO_URL} alt="Logo de GALOP Solutions" width={64} height={64} loading="lazy" decoding="async" className="w-16 h-16 object-contain -my-4" data-id="element-173" data-mp="3krprcz0qcb42" />
        </div>

        <div className="text-center md:text-left text-sm" data-id="element-174" data-mp="1j17ewbw0wxho">
          <p data-id="element-175" data-mp="1e1mf5z914l3w">
            © {new Date().getFullYear()} GALOP Solutions. Tous droits réservés.
          </p>
        </div>

        <div className="flex gap-6 text-sm" data-id="element-176" data-mp="191gksbbdkki4">
          <button type="button" onClick={toggleLegal} aria-expanded={legalOpen} className="hover:text-[#F5EBD7] transition-colors" data-id="element-177" data-mp="194ch5v7kr14g">
            Mentions légales
          </button>
          <button type="button" onClick={togglePrivacy} aria-expanded={privacyOpen} className="hover:text-[#F5EBD7] transition-colors" data-id="element-178" data-mp="1wravam881wz7">
            Confidentialité
          </button>
        </div>
      </div>
    </footer>;
};
// --- Main Page ---
export function Home() {
  useSEO();
  return <>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-[#2B1D14] focus:text-[#F5EBD7] focus:rounded-lg focus:font-semibold" data-id="element-179" data-mp="2kjhx40wpuw60">
        Aller au contenu principal
      </a>
      <main id="main-content" className="w-full min-h-screen bg-white font-sans selection:bg-[#2B1D14] selection:text-[#F5EBD7]" data-id="element-180" data-mp="irg4roa9n9id">
        <Header data-id="element-181" data-mp="91wie8ncgxir" />
        <Hero data-id="element-182" data-mp="91egowhwxr3g" />
        <Services data-id="element-183" data-mp="90v846e5kf23" />
        <Process data-id="element-184" data-mp="90d42nl90wjm" />
        <About data-id="element-185" data-mp="3r82j2elkw05f" />
        <Contact data-id="element-186" data-mp="3r7jd7w97d27r" />
        <Footer data-id="element-187" data-mp="3r6zu4c7tg7s5" />
      </main>
    </>;
}